// Turning a report request into rows.
//
// Pure, like the rest of the domain: it takes the data it needs as arguments and
// returns a table, so it is testable without an emulator and runs unchanged in
// the functions/domain/ mirror (functions/documents/link.js asks it).
//
// A catalogue entry the app's data cannot answer — Sales Tax, which no record
// carries — returns `supported: false` with a reason rather than an empty
// table, because "no rows" and "this cannot be computed" mean very different
// things to someone reconciling their books.
//
// ── The runner is `core/src/reports.rs`'s ──────────────────────────────────
//
// Every case — attendance counted through the core's `attended` and
// `teacher_cancelled` columns, billable hours and Lessons Provided's
// `Billable` through `is_billable_for` with the lesson's own flag (#362), the
// Make-Up Credits report through the studio's policy and the calendar half of
// `Booked` (#363), cash against accrual, the tax credit receipt's payments net
// of refunds, and the refusal of an unbounded range for the three reports
// that walk expanded lessons — is `run_report` in the core, so a phone that
// creates a report produces the same table.
//
// What is here is the crossing: the providers' documents coerced into the
// core's records, `statusFor` asked for every student on every lesson, and the
// cells turned back into the row objects the result drawer, the CSV and the
// PDF service receive.
import { coreSync } from './core.js';
import { todayISO } from './clock.js';
import { documentToRows } from './documents.js';
import { tombstoneOf } from './tombstone.js';
import { mileageTripOf } from './mileage.js';
import { entryOf } from './expenses.js';

const str = (v) => (v === null || v === undefined ? null : String(v));
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// A report's parameters as the core's rows: a text or a radio as a string, a
// checkbox as a boolean, the Students picker as a list.
export const paramRows = (params = {}) => documentToRows(params || {});

// The studio the runner reads, from the providers' shapes. `ctx` is
// `{ students, families, ledgerEntries, makeupEntries, occurrencesBetween,
// statusFor, nameFormats, makeupPolicy, practiceEntries, repertoire, today,
// currency, mileageEntries, distanceUnit, mileageRate, expenseEntries,
// datePattern, timeText }`.
//
// `occurrencesBetween(from, to)` is `useEvents`'s: the series expanded over a
// window, both ends inclusive. The runner asks it — not the caller — and only
// for the window the core says the report reads (`reportLessonWindow`, #625):
// the report's own range for Attendance, Billable Hours and Lessons
// Provided, the booking window for Make-Up Credits, and nothing for the
// rest. Left off, there are no lessons.
//
// `timeText` is `(hhmm) => string`, the studio's clock (`formatTime` in its
// locale): Lessons Provided writes each lesson's start in it, `4:00 PM` in the
// United States (#618). Left off, the stored `HH:MM`.
//
// `currency` is the studio's, `currencyFor(locale)`: it decides how many places
// a money cell is written to. Left off, the core writes dollars and cents,
// which is a hundredfold wrong for yen.
//
// `mileageEntries` are the Mileage Log's trips and `distanceUnit` the studio's
// `distanceUnitFor(locale)`, which the Mileage report converts every trip to
// (#476). Left off, there are no trips, and the unit is miles. `mileageRate`
// is Business Settings' Rate in ten-thousandths, which the Mileage report
// writes beside a trip's Cost (#521); left off, the Rate column is empty.
function studioArgs(ctx = {}, occurrences = []) {
  const statusFor = typeof ctx.statusFor === 'function' ? ctx.statusFor : () => null;
  // Once per distinct start, not once per lesson: a year's range is every
  // weekly lesson in it, and a studio's lessons start at a handful of times.
  // Formatting each one builds a fresh clock formatter, thousands of times over
  // a long range (#618).
  const clock = typeof ctx.timeText === 'function' ? ctx.timeText : () => null;
  const clockOf = new Map();
  const timeText = (hhmm) => {
    if (!clockOf.has(hhmm)) clockOf.set(hhmm, clock(hhmm));
    return clockOf.get(hhmm);
  };
  const lessonStudents = [];
  const attendance = [];
  occurrences.forEach((o, i) => {
    for (const sid of o?.studentIds || []) {
      lessonStudents.push({ lesson: i, studentId: String(sid) });
      const status = statusFor(o.id, sid);
      if (status) attendance.push({ occurrenceId: String(o.id), studentId: String(sid), status: String(status) });
    }
  });
  const policy = ctx.makeupPolicy || {};
  return [
    (ctx.students || []).map((s) => ({
      id: String(s?.id ?? ''),
      firstName: str(s?.firstName),
      lastName: str(s?.lastName),
      status: str(s?.status),
      email: str(s?.email),
      phone: str(s?.phone),
      categoryLabel: str(s?.categoryLabel),
      durationMin: typeof s?.durationMin === 'number' ? s.durationMin : null,
      pricingMode: str(s?.pricingMode),
      rateMinor: Number(s?.rateMinor) || 0,
      studentSince: str(s?.studentSince),
    })),
    (ctx.families || []).map((f) => ({ id: String(f?.id ?? ''), lastName: str(f?.lastName) })),
    (ctx.ledgerEntries || []).map((e) => ({
      id: String(e?.id ?? ''),
      familyId: str(e?.familyId),
      kind: String(e?.kind ?? ''),
      amountMinor: Number(e?.amountMinor) || 0,
      occurredOn: str(e?.occurredOn),
      description: str(e?.description),
      voided: Boolean(e?.voided),
      voidedAt: str(e?.voidedAt),
    })),
    (ctx.makeupEntries || []).map((m) => ({
      studentId: str(m?.studentId),
      id: str(m?.id),
      kind: str(m?.kind),
      durationMin: num(m?.durationMin),
      occurredOn: str(m?.occurredOn),
      ...tombstoneOf(m),
      sourceType: str(m?.sourceType),
      slot: num(m?.slot),
    })),
    occurrences.map((o) => ({
      id: String(o?.id ?? ''),
      date: String(o?.date ?? ''),
      startTime: str(o?.startTime),
      durationMin: num(o?.durationMin),
      requiresMakeupCredit: o?.requiresMakeupCredit === true,
      timeText: o?.startTime ? str(timeText(String(o.startTime))) : null,
    })),
    lessonStudents,
    attendance,
    (ctx.practiceEntries || []).map((p) => ({
      studentId: str(p?.studentId),
      practicedOn: str(p?.practicedOn),
      minutes: num(p?.minutes),
      ...tombstoneOf(p),
    })),
    (ctx.repertoire || []).map((r) => ({
      studentId: str(r?.studentId),
      title: str(r?.title),
      composer: str(r?.composer),
      status: str(r?.status),
      assignedOn: str(r?.assignedOn),
    })),
    str(ctx.nameFormats?.student),
    num(policy.maxCredits),
    num(policy.expireAfterDays),
    String(ctx.today || todayISO()),
    str(ctx.currency),
    (ctx.mileageEntries || []).map(mileageTripOf),
    str(ctx.distanceUnit),
    // Expenses and other revenue, as the Expenses page reads them: Revenue &
    // Expenses covers them beside the ledger (#503).
    (ctx.expenseEntries || []).map(entryOf),
    // The studio's short-date pattern (`shortDatePattern`): a report's dates
    // are written in it, as the screen writes them. Absent, as stored.
    str(ctx.datePattern),
    // Business Settings' Mileage Log Rate in ten-thousandths: the Mileage
    // report's Rate where it makes a trip's Cost (#521). Absent, no rate.
    num(ctx.mileageRate),
  ];
}

const cellValue = (c) => (c.kind === 'number' ? c.number : c.kind === 'string' ? c.text : null);

/** The core's cells as the row objects every reader receives. */
export function rowsFromCells(cells = []) {
  const rows = [];
  for (const c of cells) {
    rows[c.row] = rows[c.row] || {};
    rows[c.row][c.column] = cellValue(c);
  }
  return rows;
}

// The lessons a report reads, expanded over the window the core names and no
// further: none at all for a type that reads none (#625).
function lessonsFor(typeId, rows, ctx) {
  const reads = coreSync().reportLessonWindow(typeId, rows, String(ctx.today || todayISO()));
  if (!reads.reads || typeof ctx.occurrencesBetween !== 'function') return [];
  return ctx.occurrencesBetween(reads.from, reads.to) || [];
}

// Whether a report may be created yet (#646): `{ waiting, reason }`, the lists
// it reads that are not among `read` and what a disabled Create says, or
// `{ waiting: [], reason: null }` once it may. `read` names the lists this
// device has read (`INPUTS` in core/src/reports.rs: `students`, `families`,
// `ledger`, `makeup`, `schedule`, `attendance`, `practice`, `repertoire`,
// `mileage`, `expenses`, `settings`); a list not named is unread, so one a
// caller forgets holds Create back rather than letting a report be folded
// over it. A report is stored finished and cannot be updated, so one run
// over a list that has not answered yet is wrong for good.
export function reportWaiting(typeId, params, read, today = todayISO()) {
  const w = coreSync().reportWaiting(String(typeId ?? ''), paramRows(params), String(today), [...read].map(String));
  return { waiting: w.waiting || [], reason: w.reason ?? null };
}

// `{ supported, columns, rows, basis? }`, or `{ supported: false, reason }`.
// One fold for all three answers — the verdict, the columns and the cells
// (`runReport` in the core).
export function runReport(typeId, params, ctx = {}) {
  const id = String(typeId ?? '');
  const rows = paramRows(params);
  const run = coreSync().runReport(id, rows, ...studioArgs(ctx, lessonsFor(id, rows, ctx)));
  const { outcome } = run;
  if (!outcome.supported) return { supported: false, reason: outcome.reason ?? null };
  const out = { supported: true, columns: run.columns };
  if (outcome.basis != null) out.basis = outcome.basis;
  out.rows = rowsFromCells(run.cells);
  return out;
}

// Include Attachments (#623). Whether a report's PDF appends the receipts of
// the entries it lists — Revenue & Expenses with the box ticked — and which
// entries those are, in the report's order, each `{ id, date, payee, amount }`
// with the caption written as the report writes the line (`attached_entries`
// in core/src/reports.rs). `ctx` is `runReport`'s; only `expenseEntries`,
// `currency` and `datePattern` matter to it. The server asks these
// (functions/documents/link.js), from the studio's entries as they stand.
export function reportAttachesReceipts(typeId, params) {
  return coreSync().reportAttachesReceipts(String(typeId ?? ''), paramRows(params));
}

export function reportAttachedEntries(typeId, params, ctx) {
  return coreSync().reportAttachedEntries(String(typeId ?? ''), paramRows(params), ...studioArgs(ctx));
}

// Which columns hold money — `money_columns` in the core, beside the code that
// produces them, because that is the only place that knows. The values are
// decimal strings in the studio's currency's places (`12.34`, `1234` for yen,
// `1.234` for dinars), not minor units; see `moneyEncoding` in
// services/report-pdf, which reads them back with the same currency.
export function moneyColumnsFor(typeId) {
  return coreSync().reportMoneyColumns(String(typeId ?? ''));
}

// { Balance: 'money' }, the shape a renderer wants.
export function columnTypesFor(typeId) {
  const out = {};
  for (const c of moneyColumnsFor(typeId)) out[c] = 'money';
  return out;
}

// A stored cell as the result table shows it (#502): a money column's figure
// — the runner writes it as a decimal string for the CSV, `62.40` — in the
// studio's money, as every other amount on screen; any other figure — hours,
// credits, a distance — with the Region's `decimal` separator (`1,50` in
// Germany, #549: `reportCellShown`, the core's `figure_column`); anything
// else, and a money cell that holds words ("No charge"), as it is.
// `reportMoneyCellMinor` is the core's reading of the figure back into minor
// units of `currency`.
export function reportCellText(typeId, column, value, { currency, formatMoney, decimal }) {
  if (value === null || value === undefined) return '';
  if (moneyColumnsFor(typeId).includes(column)) {
    const shown = csvMoney({ currency, formatMoney })(value);
    if (shown !== null) return shown;
  }
  return coreSync().reportCellShown(String(typeId ?? ''), String(column), String(value), decimal);
}

// A row object's values as the core's cells, a money column's carrying the
// studio's money for it (`shownMoney`) when `money` has one.
function cellsOf(typeId, rows = [], money = () => null) {
  const moneyColumns = moneyColumnsFor(typeId);
  const cells = [];
  rows.forEach((r, row) => {
    for (const [column, v] of Object.entries(r || {})) {
      if (typeof v === 'number') cells.push({ row, column, kind: 'number', text: null, number: v });
      else if (v === null || v === undefined) cells.push({ row, column, kind: 'null', text: null, number: 0 });
      else {
        const cell = { row, column, kind: 'string', text: String(v), number: 0 };
        const shown = moneyColumns.includes(column) ? money(String(v)) : null;
        if (typeof shown === 'string') cell.shownMoney = shown;
        cells.push(cell);
      }
    }
  });
  return cells;
}

// A money cell's stored figure (`5.71`) in the studio's money (`5,71 €`), or
// null for one that is not a figure ("No charge"): what `toCSV` is handed as
// `money`, from the same reading the table makes (`reportCellText`).
export function csvMoney({ currency, formatMoney }) {
  return (text) => {
    const minor = coreSync().reportMoneyCellMinor(String(text), String(currency ?? ''));
    return minor === null || minor === undefined ? null : formatMoney(minor);
  };
}

// CSV for the "CSV (Excel)" format — `to_csv` in the core. A report of
// `typeId`, its figures with the Region's `decimal` separator, as the table
// shows them (#549). In a point Region, RFC 4180: a field holding a comma, a
// quote or a newline quoted, so a student named "Smith, John" does not
// silently become two columns. In a comma Region, MMS's file (#588): `;`
// between fields, a bare `1,50`, money as the studio writes it (`5,71 €`,
// from `money`, which is `csvMoney`'s), a byte-order mark and CRLF.
export function toCSV(typeId, columns, rows, decimal, money) {
  return coreSync().reportCsv(String(typeId ?? ''), (columns || []).map(String), cellsOf(typeId, rows, money), decimal);
}
